import React, { useEffect, useState, useCallback } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import RestService from '../Services/RestService';

// Receipt page the user lands on after returning from Vipps. The Vipps returnUrl points here
// with ?ref=<reference>; we poll the backend, which reconciles the payment with Vipps (and
// captures it once authorized).

interface PaymentStatus {
  Id: number;
  Reference: string;
  Status: string;
  VippsStatus: string;
  Amount: number;
  ProductName: string;
}

const PENDING_STATES = ['INITIATED', 'AUTHORIZED'];

const MedlemskontingentKvitteringView: React.FC<RouteComponentProps> = ({ location }) => {
  const reference = new URLSearchParams(location.search).get('ref');
  const [status, setStatus] = useState<PaymentStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);

  const fetchStatus = useCallback(async () => {
    if (!reference) {
      setError('Mangler betalingsreferanse.');
      return;
    }
    try {
      const data = await RestService.get<PaymentStatus>(`/api/payment/status/${reference}`);
      setStatus(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Kunne ikke hente betalingsstatus.');
    }
  }, [reference]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Re-poll a few times while the payment is still pending (capture can lag a moment).
  useEffect(() => {
    if (!status || !PENDING_STATES.includes(status.Status) || attempts >= 5) return;
    const t = setTimeout(() => {
      setAttempts((a) => a + 1);
      fetchStatus();
    }, 2000);
    return () => clearTimeout(t);
  }, [status, attempts, fetchStatus]);

  const renderBody = () => {
    if (error) {
      return <p className="ods-text--error">{error}</p>;
    }
    if (!status) {
      return <p>Henter betalingsstatus …</p>;
    }
    switch (status.Status) {
      case 'PAID':
        return (
          <>
            <h1 className="ods-status-card__heading">Takk for betalingen!</h1>
            <p>
              Betalingen av <strong>{status.ProductName}</strong> på{' '}
              <strong>{status.Amount} kr</strong> er gjennomført. Medlemskapet er nå aktivt.
            </p>
          </>
        );
      case 'CANCELLED':
        return (
          <>
            <h1 className="ods-status-card__heading">Betalingen ble avbrutt</h1>
            <p>Betalingen ble ikke fullført. Du kan prøve igjen når du vil.</p>
          </>
        );
      case 'EXPIRED':
        return (
          <>
            <h1 className="ods-status-card__heading">Betalingen utløp</h1>
            <p>Betalingsforespørselen utløp. Du kan starte en ny betaling.</p>
          </>
        );
      default:
        return (
          <>
            <h1 className="ods-status-card__heading">Behandler betalingen …</h1>
            <p>Vi venter på bekreftelse fra Vipps. Dette tar normalt bare et øyeblikk.</p>
          </>
        );
    }
  };

  return (
    <div className="ods-margin-top-25 ods-grid ods-grid--gap ods-grid--center">
      <div className="ods-grid__column--12 ods-grid__column--start-3-breakpoint-medium ods-grid__column--end-11-breakpoint-medium">
        <div className="ods-status-card">
          <div className="ods-status-card__content">
            {renderBody()}
            <p className="ods-margin-top-19 ods-text--align-center">
              <a href="/medlemskontingent" className="o-kom-link">Tilbake til medlemskontingent</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withRouter(MedlemskontingentKvitteringView);
